/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react'

import React from 'react'
import { View, BoxView, ActionButton, Text, TextField, Button } from '@components/atoms'
// NOTE: OrderViewTab(세로 탭) 임시 숨김 처리 — 테이블 뷰만 사용
// import { OrderViewTab } from '@components/systems'
import { Grid, orderColumns } from '@grid'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import {
	Select,
	SelectChangeEvent,
	MenuItem,
	FormControl,
	InputLabel,
} from '@mui/material'

import { GridRenderCellParams, GridRowSelectionModel } from '@mui/x-data-grid'
import { useAppSelector, useAppDispatch, orderActions } from '@redux/index'
import { OrderType } from '@schemas'
import dayjs from 'dayjs'
import { blue, grey, orange, pink, teal, yellow } from '@mui/material/colors'

export const OrderGrid = () => {
	const reduxDispatch = useAppDispatch()

	// redux state
	const orderList: OrderType[] = useAppSelector(state => state.order.orderList)
	const selectedOrderIds: string[] = useAppSelector(state => state.order.selectedOrderIds)
	const selectedStatusArr: string[] = useAppSelector(
		state => state.order.selectedStatusArr,
	)
	const searchStartDate: string = useAppSelector(state => state.order.searchStartDate)
	const searchEndDate: string = useAppSelector(state => state.order.searchEndDate)
	const searchStatus: string = useAppSelector(state => state.order.searchStatus)
	const searchKeyword: string = useAppSelector(state => state.order.searchKeyword)

	// 선택된 오더 파생 데이터
	const selectedOrders = React.useMemo(
		() => orderList.filter(o => selectedOrderIds.includes(o.id)),
		[orderList, selectedOrderIds],
	)
	const hasSelection = selectedOrderIds.length >= 1

	// 버튼 visibility 조건
	const showOrderPreparing =
		hasSelection && selectedStatusArr.length === 1 && selectedStatusArr[0] === 'new'
	const showLabelPrinting = hasSelection && selectedOrders.every(o => !!o.trackingNumber)
	const showUpdateTransit =
		hasSelection && selectedStatusArr.every(s => ['preparing', 'inTransit'].includes(s))

	const onRowSelectionModelChange = (newRowSelectionModel: GridRowSelectionModel) => {
		reduxDispatch(orderActions.SELECTED_ORDER_IDS(newRowSelectionModel as string[]))
	}

	const onStartDateChange = (date: any) => {
		const newDate = dayjs(date).format('YYYY-MM-DD')
		reduxDispatch(orderActions.SEARCH_START_DATE(newDate))
	}

	const onEndDateChange = (date: any) => {
		const newDate = dayjs(date).format('YYYY-MM-DD')
		reduxDispatch(orderActions.SEARCH_END_DATE(newDate))
	}

	const onStatusChange = (e: SelectChangeEvent) => {
		const newStatus = e.target.value
		reduxDispatch(orderActions.SEARCH_STATUS(newStatus))
	}

	const onKeywordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const str = e.target.value
		reduxDispatch(orderActions.SEARCH_KEYWORD(str))
	}

	const onOrderPreparingClick = () => {
		reduxDispatch(orderActions.TRIGGER_ORDER_PREPARING(true))
	}

	const onOrderLabelPrintingClick = () => {
		reduxDispatch(orderActions.TRIGGER_ORDER_LABEL_PRINTING(true))
	}

	const onUpdateOrderTransitClick = () => {
		reduxDispatch(orderActions.TRIGGER_UPDATE_ORDER_TRANSIT(true))
	}

	const onDeleteOrderClick = () => {
		reduxDispatch(orderActions.TRIGGER_DELETE_ORDER(true))
	}

	const onRefundClick = () => {
		reduxDispatch(orderActions.TRIGGER_REFUND_ORDER(true))
	}

	return (
		<View css={style}>
			{/* OrderViewTab(세로 탭) 임시 숨김 처리 — 테이블 뷰만 사용 */}
			<BoxView
				title="오더 관리"
				className="orderGirdBoxView"
				leftHeaderAccessory={
					<View className="orderGridLeftBoxTopView">
						<Button
							variant="contained"
							className="orderSubmitButton"
							onClick={() => {
								reduxDispatch(orderActions.TRIGGER_ORDER_SEARCH(true))
							}}
						>
							검색
						</Button>
						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<View className="datePickerView">
								<DatePicker
									className="datePicker"
									label="시작일자"
									value={new Date(searchStartDate)}
									format="yyyy-MM-dd"
									onChange={onStartDateChange}
									slotProps={{
										textField: {
											variant: 'outlined',
											size: 'small',
											style: {
												width: '8rem',
											},
											placeholder: 'YYYY-MM-DD',
										},
									}}
								/>
								<Text className="datePickerText">~</Text>
								<DatePicker
									className="datePicker"
									label="종료일자"
									format="yyyy-MM-dd"
									value={new Date(searchEndDate)}
									onChange={onEndDateChange}
									slotProps={{
										textField: {
											variant: 'outlined',
											size: 'small',
											style: {
												width: '8rem',
											},
											placeholder: 'YYYY-MM-DD',
										},
									}}
								/>
							</View>
						</LocalizationProvider>

						<View className="orderGridSearchView">
							<FormControl fullWidth>
								<InputLabel id="delivery-state-select-label">상태</InputLabel>
								<Select
									className="categSelect"
									name="categ"
									label="상태"
									value={searchStatus}
									size="small"
									onChange={onStatusChange}
								>
									<MenuItem value="all">전체</MenuItem>
									<MenuItem value="new">신규 주문</MenuItem>
									<MenuItem value="preparing">배송 준비중</MenuItem>
									<MenuItem value="inTransit">배송중</MenuItem>
									<MenuItem value="completed">배송완료</MenuItem>
									<MenuItem value="refunded">환불</MenuItem>
									<MenuItem value="deleted">삭제</MenuItem>
								</Select>
							</FormControl>

							<TextField
								type="text"
								label="검색"
								size="small"
								value={searchKeyword}
								onChange={onKeywordChange}
							/>
						</View>
					</View>
				}
				rightHeaderAccessory={
					<View className="orderGridRightBoxTopView">
						{showOrderPreparing && (
							<ActionButton
								onClick={onOrderPreparingClick}
								title="주문확인"
								iconName="check-circle-outline"
								iconTextColor={grey[700]}
							/>
						)}

						{showLabelPrinting && (
							<ActionButton
								onClick={onOrderLabelPrintingClick}
								title="라벨 출력"
								iconName="printer-pos-outline"
								iconTextColor={grey[700]}
							/>
						)}

						{showUpdateTransit && (
							<ActionButton
								onClick={onUpdateOrderTransitClick}
								title="배송 업데이트"
								iconName="truck-delivery-outline"
								iconTextColor={grey[700]}
							/>
						)}

						<ActionButton
							onClick={onDeleteOrderClick}
							title="삭제"
							disabled={!hasSelection}
							iconName="delete-outline"
							iconTextColor={grey[700]}
						/>

						<ActionButton
							onClick={onRefundClick}
							title="환불"
							disabled={!hasSelection}
							iconName="cash-refund"
							iconTextColor={grey[700]}
						/>
					</View>
				}
			>
				<Grid
					title="오더 관리"
					rows={orderList}
					columns={orderColumns}
					loading={status === 'loading'}
					isEditable={false}
					checkboxSelection={true}
					rowSelectionModel={selectedOrderIds}
					onRowSelectionModelChange={onRowSelectionModelChange}
					renderCell={{
						status: (params: GridRenderCellParams<OrderType, any, any>) => {
							if (params.value === null) {
								return <></>
							}

							const statusTitle =
								params.value === 'new'
									? '신규주문'
									: params.value === 'preparing'
									? '배송준비중'
									: params.value === 'inTransit'
									? '배송중'
									: params.value === 'completed'
									? '완료'
									: params.value === 'deleted'
									? '삭제'
									: params.value === 'refunded'
									? '환불'
									: '신규주문'

							return (
								<View className={`statusView ${params.value}`}>
									<Text className="statusTitle">{statusTitle}</Text>
								</View>
							)
						},
						invoiceUrl: (params: GridRenderCellParams<OrderType, any, any>) => {
							return (
								<a href={params.value} target="_blank" rel="noreferrer">
									인보이스 링크
								</a>
							)
						},
					}}
					renderEditCell={
						{
							// Add any custom edit cell rendering if needed
						}
					}
				/>
			</BoxView>
		</View>
	)
}

const style = css({
	height: '100%',
	margin: 'auto',
	// 세로 탭 숨김으로 기존 paddingLeft '1.5rem' 제거 — MainLayout container의 좌우 0.5rem 패딩으로 대칭
	background: 'white',
	borderRadius: '5px',

	'.orderGridLeftBoxTopView': {
		display: 'flex',
		alignItems: 'center',

		'.orderSubmitButton': {
			marginRight: '0.5rem',
			marginBottom: '-0.3rem',
		},
		'.datePickerView': {
			display: 'flex',
			alignItems: 'center',
			marginBottom: '-0.3rem',

			'.datePicker': {
				width: '9rem',

				'.MuiInputBase-input': {
					fontSize: '11px',
					height: '1.5rem',
				},
			},

			'.datePickerText': {
				margin: '0 0.5rem',
			},
		},

		'.orderGridSearchView': {
			marginLeft: '0.5rem',
			marginBottom: '-0.3rem',
			gap: '0.5rem',
			display: 'flex',
			alignItems: 'center',
		},
	},
	'.orderGridRightBoxTopView': {
		display: 'flex',
		alignItems: 'center',
		width: '100%',
	},

	'.statusView': {
		height: 30,
		paddingTop: 6,
		background: grey[100],

		'.statusTitle': {
			margin: 0,
		},

		'&.new': {
			backgroundColor: grey['800'],
			color: 'white',
		},

		'&.preparing': {
			backgroundColor: yellow['A400'],
			color: grey[900],
		},

		'&.inTransit': {
			backgroundColor: teal['A400'],
			color: 'white',
		},

		'&.completed': {
			backgroundColor: blue['A400'],
			color: 'white',
		},

		'&.deleted': {
			backgroundColor: pink['A400'],
			color: 'white',
		},

		'&.refunded': {
			backgroundColor: orange['A400'],
			color: 'white',
		},
	},
})
